import { z } from 'zod';

import { request } from '@/shared/utils/requestBuilder';
import { assertUrlNavegavelSegura } from '@/shared/utils/urlSegura';

import type {
  AlvoDoPacote,
  ArquivoAssinado,
  ArquivoDoPacote,
  AutosDoProcesso,
  ChaveDaSecao,
  MotivoDaRecusa,
  OpcoesDosAutos,
  PacoteDosAutos,
  RegistroDeAnexos,
} from '../types/baixarProcesso.types';
import { rotaBaseDosAutos } from '../utils/rotaDosAutos';
import { CHAVES_DA_SECAO } from '../utils/secoesDownload';

/**
 * Autos em PDF gerados no SERVIDOR (`/arbitral/processos/:id/autos-pdf…` para o escritório e o
 * espelho `/admin/arbitral/…` para a equipe interna). A geração é assíncrona: o POST devolve o
 * pacote da versão atual (novo ou o que a versão já tinha) e a tela acompanha pelo GET até
 * `PRONTO`. Quem decide reaproveitar é o servidor — nada é guardado aqui.
 *
 * Este serviço só fala com o servidor. QUANTO esperar é decisão da tela (`useEsperaDoPacote`):
 * ela acompanha, pergunta se a pessoa quer continuar esperando e, sem resposta, pede o aviso por
 * e-mail — o servidor manda o e-mail quando o pacote terminar, pronto ou com falha.
 */

const INTERVALO_MS = 2500;
const TIMEOUT_REQUISICAO_MS = 15000;
/**
 * Consultas seguidas que podem falhar (rede, gateway, timeout) antes de desistir de acompanhar.
 * Depois do "Continuar esperando" o acompanhamento não tem prazo — sem tolerância, uma consulta
 * perdida em centenas derrubava a espera de um pacote que o servidor seguia gerando.
 */
const FALHAS_SEGUIDAS_TOLERADAS = 3;

/** O servidor parou de responder às consultas; o pacote pode estar sendo gerado normalmente. */
export class SemRespostaDoServidorError extends Error {
  constructor(options?: { cause?: unknown }) {
    super('O servidor parou de responder enquanto o pacote era gerado.', options);
    this.name = 'SemRespostaDoServidorError';
  }
}

/**
 * Recusa do servidor com o motivo que a tela precisa para escolher o estado. `erroCodigo`: o do
 * pacote que terminou em `FALHOU`, quando a recusa veio dele.
 */
export class RecusaDosAutosError extends Error {
  readonly motivo: MotivoDaRecusa;
  readonly erroCodigo: string | null;

  constructor(motivo: MotivoDaRecusa, mensagem: string, erroCodigo: string | null = null) {
    super(mensagem);
    this.name = 'RecusaDosAutosError';
    this.motivo = motivo;
    this.erroCodigo = erroCodigo;
  }
}

/** O pacote terminou em `FALHOU`: a mensagem para a tela e o código, para ela decidir o que oferecer. */
export function recusaDoPacoteQueFalhou(erroCodigo: string | null): RecusaDosAutosError {
  return new RecusaDosAutosError('erro', mensagemDoPacoteQueFalhou(erroCodigo), erroCodigo);
}

const chaveDaSecaoSchema = z.enum(CHAVES_DA_SECAO);

// Campos novos aceitam ausência: o front não pode quebrar o PDF completo se subir antes do core.
const nuloSeAusente = <T extends z.ZodType>(schema: T) =>
  schema.nullish().transform((valor) => valor ?? null);

const parteSchema = z.object({
  numero: z.number().int().positive(),
  paginaInicial: z.number().int(),
  totalPaginas: z.number().int(),
  tamanhoBytes: z.number(),
  excedeLimite: z.boolean(),
});

const pacoteBaseSchema = z.object({
  pacoteId: z.string(),
  status: z.enum(['PENDENTE', 'GERANDO', 'PRONTO', 'FALHOU']),
  codigoAutenticacao: z.string(),
  secoes: nuloSeAusente(z.array(chaveDaSecaoSchema)),
  totalPaginas: z.number().nullable(),
  tamanhoBytes: z.number().nullable(),
  erroCodigo: z.string().nullable(),
  criadoEm: nuloSeAusente(z.string()),
  partes: nuloSeAusente(z.array(parteSchema)),
  tamanhoZipBytes: nuloSeAusente(z.number()),
  anexosNaoIncorporados: nuloSeAusente(z.number().int()),
  // Ausente na listagem e no core anterior à ciência: sem ela, nada a confirmar.
  cienciaPendente: z
    .boolean()
    .nullish()
    .transform((valor) => valor ?? false),
});

const registroDeAnexosSchema = z.object({
  disponivel: z.boolean(),
  anexos: z.array(
    z.object({
      anexoId: z.string(),
      nome: z.string(),
      tipoMime: z.string(),
      eventoNumero: z.string().nullable(),
      // Tratamento novo do servidor degrada só o anexo dele (para "não entrou"), e não o registro
      // inteiro: sem o registro, a ciência não pode ser dada e o download fica travado.
      tratamento: z
        .enum(['INCORPORAR', 'AVISO_SIGILOSO', 'AVISO_FORMATO', 'AVISO_ILEGIVEL'])
        .catch('AVISO_FORMATO'),
      motivo: z.string().nullable(),
      convertido: z.boolean(),
      paginaInicial: z.number().int(),
      totalPaginas: z.number().int(),
    }),
  ),
  naoIncorporados: z.number().int(),
  cienciaPendente: z.boolean(),
  cienciaEm: z.string().nullable(),
});

const cienciaSchema = z.object({ cienciaEm: z.string() });

const pacoteNaPortaSchema = pacoteBaseSchema.extend({ urlDownload: z.string().nullable() });

const opcoesSchema = z.object({
  secoes: z.array(
    z.object({ chave: chaveDaSecaoSchema, titulo: z.string(), disponivel: z.boolean() }),
  ),
  pacotes: z.array(pacoteBaseSchema.extend({ atual: z.boolean() })),
});

const arquivoAssinadoSchema = z.object({ url: z.string(), nomeArquivo: z.string() });

const envelopeSchema = z.object({ success: z.literal(true), data: z.unknown() });

const MENSAGEM_POR_ERRO: Record<string, string> = {
  VERSAO_DESATUALIZADA: 'O processo teve movimentação durante a geração. Gere de novo.',
  LIMITE_EXCEDIDO: 'O processo é grande demais para um PDF único.',
  PREPARACAO_EXPIRADA: 'A geração demorou além do esperado. Tente de novo.',
  EXECUCAO_SEM_RESULTADO: 'A geração foi interrompida. Tente de novo.',
};

const MENSAGEM_GENERICA = 'Não foi possível gerar o PDF do processo.';

/** O que dizer de um pacote que terminou em `FALHOU`, pelo código que o servidor gravou. */
export function mensagemDoPacoteQueFalhou(erroCodigo: string | null): string {
  return MENSAGEM_POR_ERRO[erroCodigo ?? ''] ?? MENSAGEM_GENERICA;
}

const MENSAGEM_POR_MOTIVO: Record<Exclude<MotivoDaRecusa, 'erro'>, string> = {
  semPermissao: 'Você não tem permissão para baixar os autos deste processo.',
  indisponivel: 'O download dos autos ainda não está disponível para este processo.',
  secoesIndisponiveis:
    'Alguma seção marcada deixou de estar disponível antes da confirmação. Revise a seleção.',
  cienciaPendente: 'Confirme a ciência dos anexos que não entraram no PDF antes de baixar.',
};

/**
 * Só o 403 é falta de permissão. O 404 vira erro: o processo da tela é sempre um que a pessoa
 * abriu, então 404 é pacote que sumiu ou rota que ainda não subiu — dizer "sem permissão" aí
 * esconderia um defeito de deploy do suporte. O 409 tem duas causas:
 * a seção que sumiu entre a seleção e a confirmação (o corpo traz `SECOES_INDISPONIVEIS`) e o
 * download desligado para o escritório.
 */
async function recusaDaResposta(response: Response): Promise<RecusaDosAutosError> {
  const corpo = await response.text().catch(() => '');
  if (response.status === 403) {
    return new RecusaDosAutosError('semPermissao', MENSAGEM_POR_MOTIVO.semPermissao);
  }
  if (response.status === 409) {
    const motivo = motivoDoConflito(corpo);
    return new RecusaDosAutosError(motivo, MENSAGEM_POR_MOTIVO[motivo]);
  }
  return new RecusaDosAutosError('erro', mensagemDoCorpo(corpo) ?? MENSAGEM_GENERICA);
}

/** As três causas do 409, pelo código que o corpo traz. */
function motivoDoConflito(
  corpo: string,
): 'secoesIndisponiveis' | 'cienciaPendente' | 'indisponivel' {
  if (corpo.includes('SECOES_INDISPONIVEIS')) return 'secoesIndisponiveis';
  if (corpo.includes('CIENCIA_PENDENTE')) return 'cienciaPendente';
  return 'indisponivel';
}

function mensagemDoCorpo(corpo: string): string | null {
  try {
    const lido = JSON.parse(corpo) as { error?: { message?: unknown } } | null;
    return typeof lido?.error?.message === 'string' ? lido.error.message : null;
  } catch {
    return null;
  }
}

async function lerResposta<T extends z.ZodType>(
  response: Response,
  schema: T,
): Promise<z.output<T>> {
  if (!response.ok) throw await recusaDaResposta(response);
  const envelope = envelopeSchema.safeParse(await response.json().catch(() => null));
  const lido = envelope.success ? schema.safeParse(envelope.data.data) : null;
  if (!lido?.success) throw new Error('Resposta inesperada ao gerar o PDF do processo.');
  return lido.data;
}

/**
 * Sem a recuperação de workspace do `requestBuilder`: o 403 daqui é a recusa de permissão do
 * próprio recurso, e a recuperação refaria o pedido sem o workspace — ou recarregaria a página —
 * no lugar de deixar a tela mostrar "sem permissão".
 */
const chamar = (
  endpoint: string,
  method: 'GET' | 'POST',
  opcoes: { body?: object; signal?: AbortSignal; keepalive?: boolean } = {},
) => request({ endpoint, method, ...opcoes, recuperarWorkspaceNo403: false });

/**
 * Solta o listener de `abort` a cada espera: o acompanhamento não tem prazo depois do "Continuar
 * esperando", e o mesmo `signal` acumularia um listener a cada consulta.
 */
function esperar(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) return reject(signal.reason);
    const aoAbortar = () => {
      clearTimeout(timer);
      reject(signal?.reason);
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', aoAbortar);
      resolve();
    }, ms);
    signal?.addEventListener('abort', aoAbortar, { once: true });
  });
}

function sinal(signal?: AbortSignal): AbortSignal {
  const timeout = AbortSignal.timeout(TIMEOUT_REQUISICAO_MS);
  return signal ? AbortSignal.any([signal, timeout]) : timeout;
}

const rotaDoPacote = ({ porta, processoId, pacoteId }: AlvoDoPacote) =>
  `${rotaBaseDosAutos(porta, processoId)}/${encodeURIComponent(pacoteId)}`;

export const autosPdfService = {
  /**
   * As seções que o processo tem agora (com `disponivel`) e os pacotes já gerados, do mais
   * recente; `atual` marca os da versão de agora.
   *
   * @example const { secoes } = await autosPdfService.listarAutos({ porta: 'escritorio', processoId })
   */
  listarAutos: async (autos: AutosDoProcesso, signal?: AbortSignal): Promise<OpcoesDosAutos> =>
    lerResposta(
      await chamar(rotaBaseDosAutos(autos.porta, autos.processoId), 'GET', {
        signal: sinal(signal),
      }),
      opcoesSchema,
    ),

  /**
   * Pede o pacote da versão ATUAL. Sem `secoes` é o documento completo — que também é o ZIP: o
   * pacote completo carrega as partes. O servidor devolve o pacote existente quando nada mudou;
   * pacote que volta `PRONTO` na primeira resposta é esse reaproveitamento.
   *
   * @example await autosPdfService.pedirPacote({ porta: 'secretaria', processoId }, ['partes'])
   */
  pedirPacote: async (
    autos: AutosDoProcesso,
    secoes?: readonly ChaveDaSecao[],
    signal?: AbortSignal,
  ): Promise<PacoteDosAutos> =>
    lerResposta(
      await chamar(rotaBaseDosAutos(autos.porta, autos.processoId), 'POST', {
        body: secoes ? { secoes } : undefined,
        signal: sinal(signal),
      }),
      pacoteNaPortaSchema,
    ),

  /**
   * Um pacote pelo id — o que o e-mail anunciou, ou o que a tela acompanha.
   *
   * @example await autosPdfService.consultarPacote({ porta, processoId, pacoteId })
   */
  consultarPacote: async (alvo: AlvoDoPacote, signal?: AbortSignal): Promise<PacoteDosAutos> =>
    lerResposta(
      await chamar(rotaDoPacote(alvo), 'GET', { signal: sinal(signal) }),
      pacoteNaPortaSchema,
    ),

  /**
   * Acompanha o pacote até ele terminar, sem prazo: quem desiste de esperar é a tela, abortando o
   * `signal`. `aoConsultar` recebe cada leitura — é por ela que a tela passa de "na fila" para
   * "processando". Consulta que falha não é pacote que falhou: só `FALHOU` do servidor vira erro;
   * consultas que falham seguidas viram `SemRespostaDoServidorError`. Recusa DEFINITIVA (o 403 de
   * quem perdeu o acesso no meio da espera) não é falha de rede e sai na hora: tolerada, virava
   * "servidor sem resposta" e mandava para o e-mail, que o servidor também recusa.
   *
   * @example const pronto = await autosPdfService.acompanharAteTerminar(alvo, { signal })
   */
  acompanharAteTerminar: async (
    alvo: AlvoDoPacote,
    opcoes: { signal?: AbortSignal; aoConsultar?: (pacote: PacoteDosAutos) => void } = {},
  ): Promise<PacoteDosAutos> => {
    let falhasSeguidas = 0;
    for (;;) {
      await esperar(INTERVALO_MS, opcoes.signal);
      let pacote: PacoteDosAutos;
      try {
        pacote = await autosPdfService.consultarPacote(alvo, opcoes.signal);
      } catch (erro) {
        if (erro instanceof RecusaDosAutosError && erro.motivo !== 'erro') throw erro;
        // Abortado no meio da consulta: o `esperar` da volta seguinte rejeita na hora.
        falhasSeguidas += 1;
        if (falhasSeguidas >= FALHAS_SEGUIDAS_TOLERADAS) {
          throw new SemRespostaDoServidorError({ cause: erro });
        }
        continue;
      }
      falhasSeguidas = 0;
      if (pacote.status === 'PRONTO') return pacote;
      if (pacote.status === 'FALHOU') throw recusaDoPacoteQueFalhou(pacote.erroCodigo);
      opcoes.aoConsultar?.(pacote);
    }
  },

  /**
   * URL assinada de um arquivo do pacote pronto, válida por poucos minutos — por isso é pedida no
   * clique, nunca guardada.
   *
   * @example const { url } = await autosPdfService.assinarArquivo(alvo, 'parte-2')
   */
  assinarArquivo: async (
    alvo: AlvoDoPacote,
    arquivo: ArquivoDoPacote,
  ): Promise<ArquivoAssinado> => {
    const resposta = await chamar(
      `${rotaDoPacote(alvo)}/arquivos/${encodeURIComponent(arquivo)}`,
      'GET',
      { signal: sinal() },
    );
    // Core anterior à rota de arquivos (o front pode subir antes dele): o PDF ainda sai pela URL
    // que a consulta do pacote traz, assinada na hora. Partes e ZIP não existem nesse core.
    if (arquivo === 'pdf' && resposta.status === 404) {
      const pacote = await autosPdfService.consultarPacote(alvo);
      if (pacote.urlDownload) {
        return {
          url: assertUrlNavegavelSegura(pacote.urlDownload, 'Link do arquivo dos autos'),
          nomeArquivo: `autos-${pacote.codigoAutenticacao}.pdf`,
        };
      }
    }
    const assinado = await lerResposta(resposta, arquivoAssinadoSchema);
    return {
      url: assertUrlNavegavelSegura(assinado.url, 'Link do arquivo dos autos'),
      nomeArquivo: assinado.nomeArquivo,
    };
  },

  /**
   * O registro de anexos do pacote pronto: os que não entraram (e, para a equipe interna, todos) e
   * se a pessoa ainda deve a ciência.
   *
   * @example const { anexos, cienciaPendente } = await autosPdfService.registroDeAnexos(alvo)
   */
  registroDeAnexos: async (alvo: AlvoDoPacote, signal?: AbortSignal): Promise<RegistroDeAnexos> =>
    lerResposta(
      await chamar(`${rotaDoPacote(alvo)}/anexos`, 'GET', { signal: sinal(signal) }),
      registroDeAnexosSchema,
    ),

  /**
   * "Estou ciente": registra que a pessoa viu os anexos fora do PDF e decidiu baixar sem eles. É o
   * que libera o download no servidor.
   *
   * @example const { cienciaEm } = await autosPdfService.registrarCiencia(alvo)
   */
  registrarCiencia: async (alvo: AlvoDoPacote): Promise<{ cienciaEm: string }> =>
    lerResposta(
      await chamar(`${rotaDoPacote(alvo)}/ciencia`, 'POST', { signal: sinal() }),
      cienciaSchema,
    ),

  /**
   * Pede ao servidor o e-mail de quando o pacote terminar. `aoSair`: a pessoa está fechando o
   * painel ou a aba — `keepalive` deixa a requisição terminar depois que a página some.
   *
   * @example await autosPdfService.pedirAvisoPorEmail(alvo, { aoSair: true })
   */
  pedirAvisoPorEmail: async (
    alvo: AlvoDoPacote,
    opcoes: { aoSair?: boolean } = {},
  ): Promise<void> => {
    const response = await chamar(`${rotaDoPacote(alvo)}/aviso-por-email`, 'POST', {
      signal: sinal(),
      keepalive: opcoes.aoSair === true,
    });
    // Perdeu o acesso no meio da espera: a tela diz "sem permissão", não "tente de novo".
    if (response.status === 403) throw await recusaDaResposta(response);
    if (!response.ok) {
      throw new Error('Não foi possível agendar o aviso por e-mail. Tente de novo.');
    }
  },
};
